import React from 'react';
import { observer } from 'mobx-react';
import { action, observable } from 'mobx';
import DateTimeService from '@app/Services/DateTimeService';
import { DrawMonthExtensionsHandler } from '@app/Components/FlightsGanttView/VirtualDaysList';
import { RosterZoom } from '..';
import { applyDecorators } from "@app/Helpers/Decorator";

type VirtualDateMonthProps = {
    scrollWrapperRef: React.RefObject<HTMLDivElement>;
    datesListRef: React.RefObject<HTMLDivElement>;
    day: Date;
    scrollLeft: number;
    cellWidth: number;
    width: number;
    zoom?: RosterZoom;
    highlight?: boolean;

    onRenderMonthExtensions?: DrawMonthExtensionsHandler;
};

@observer
export class VirtualDateMonth extends React.Component<VirtualDateMonthProps> {
    private _monthRef: React.RefObject<HTMLDivElement> = React.createRef();
    private _contentRef: React.RefObject<HTMLDivElement> = React.createRef();
    private _lastUpdatedScrollLeft: number = 0;
    @observable private _contentLeftProp: number = 0;

    constructor(props: VirtualDateMonthProps) {
        super(props);
        applyDecorators(this);
        this._lastUpdatedScrollLeft = props.scrollLeft;
    }
    
    componentDidMount() {
        this.props.scrollWrapperRef.current?.addEventListener('scroll', this._handleRosterScroll);
        this._contentLeftProp = this._getMonthContentLeftProp();
    }

    componentDidUpdate(prevProps: Readonly<VirtualDateMonthProps>) {
        if (prevProps.width !== this.props.width) {
            this._lastUpdatedScrollLeft = this.props.scrollLeft;
            this.updateLeftProp();
        }
    }

    componentWillUnmount() {
        this.props.scrollWrapperRef.current?.removeEventListener('scroll', this._handleRosterScroll);
    }

    render() {
        const { day, width, onRenderMonthExtensions } = this.props;

        return (
            <div ref={this._monthRef} className="person-grid-date-month" style={{ width }}>
                <div ref={this._contentRef} className="person-grid-date-month-content" style={{ left: this._contentLeftProp }}>
                    {this._renderMonthTitle(DateTimeService.toCustomUiFormat(day, 'MMMM / YYYY'))}
                    {onRenderMonthExtensions && onRenderMonthExtensions(day)}
                </div>
            </div>
        );
    }

    @action.bound
    public updateLeftProp() {
        this._contentLeftProp = this._getMonthContentLeftProp();
    }

    private _renderMonthTitle(text: string) {
        if (this.props.highlight) {
            return <b>{text}</b>;
        }

        return text;
    }

    private _getMonthContentLeftProp() {
        const datesListRect = this._getDatesListRect();
        const monthRect = this._getMonthRect();
        const contentRect = this._getContentRect();

        if (!datesListRect || !monthRect || !contentRect) return 0;

        const posStart = monthRect.left > datesListRect.left && monthRect.left < datesListRect.right ? monthRect.left : datesListRect.left;
        const posEnd = monthRect.right < datesListRect.right && monthRect.right > datesListRect.left ? monthRect.right : datesListRect.right;

        const viewportWidth = posEnd - posStart;

        const isOnLeft = posEnd < datesListRect.right;
        const isOnRight = posStart > datesListRect.left;
        const isOnMiddle = isOnLeft && isOnRight;

        if (isOnLeft) {
            const widthDiff = this.props.width - viewportWidth;
            const middlePos = viewportWidth / 2 - contentRect.width / 2;

            return widthDiff + middlePos;
        }

        if (isOnRight || isOnMiddle) {
            const middlePos = viewportWidth / 2 - contentRect.width / 2;

            if (middlePos < 0)
                return 0;

            return middlePos;
        }

        const scrollLeft = this.props.scrollWrapperRef.current?.scrollLeft || this.props.scrollLeft;
        const scrollDiff = scrollLeft % this.props.cellWidth;

        const middlePos = viewportWidth / 2 - contentRect.width / 2;

        return middlePos + scrollDiff;
    }

    @action.bound
    private _handleRosterScroll(event: Event) {
        const target = event.target as HTMLElement;

        if (Math.abs(target.scrollLeft - this._lastUpdatedScrollLeft) > this._getScrollBreakpoint()) {
            this._lastUpdatedScrollLeft = target.scrollLeft;
            this.updateLeftProp();
        }
    }

    private _getScrollBreakpoint() {
        const { zoom, cellWidth } = this.props;

        switch (zoom) {
            case RosterZoom.x1: 
                return cellWidth / 5;
            case RosterZoom.x2: 
                return cellWidth / 3;
            case RosterZoom.x4: 
                return cellWidth / 2;
            default:
                return cellWidth;
        }
    }

    private _getDatesListRect() {
        return this.props.datesListRef.current?.getBoundingClientRect();
    }
    
    private _getMonthRect() {
        return this._monthRef.current?.getBoundingClientRect();
    }

    private _getContentRect() {
        return this._contentRef.current?.getBoundingClientRect();
    }
}
